
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Clock, Navigation, Package, DollarSign, FileText, Minimize2, Maximize2 } from 'lucide-react';

interface OrderPopupProps {
  isVisible: boolean;
  isMinimized: boolean;
  onMinimize: () => void;
  onMaximize: () => void;
  onPickup: () => void;
  onDropOff: () => void;
  onNavigate: (destination: 'pickup' | 'dropoff') => void;
  order: {
    id: string;
    customerName: string;
    customerPhone: string;
    address: string;
    estimatedTime: string;
    distance: string;
    status: 'pending' | 'confirmed' | 'ready' | 'ready_for_pickup' | 'picked_up' | 'delivered';
    pickupAddress: string;
    totalAmount?: number;
    deliveryFee?: number;
    itemsTotal?: number;
    notes?: string;
  } | null;
}

const OrderPopup = ({ isVisible, isMinimized, onMinimize, onMaximize, onPickup, onDropOff, onNavigate, order }: OrderPopupProps) => {
  if (!isVisible || !order) return null;

  const getStatusText = () => {
    switch (order.status) {
      case 'confirmed':
        return 'New Order - Ready to Pickup';
      case 'ready':
        return 'On Way to Pickup';
      case 'ready_for_pickup':
        return 'Order Ready for Pickup';
      case 'picked_up':
        return 'Order Picked Up - Deliver to Customer';
      default:
        return 'Order Details';
    }
  };

  const getActionButton = () => {
    switch (order.status) {
      case 'confirmed':
      case 'ready_for_pickup':
        return (
          <Button onClick={onPickup} className="w-full bg-green-600 hover:bg-green-700">
            <Package className="h-4 w-4 mr-2" />
            Mark as Picked Up
          </Button>
        );
      case 'picked_up':
        return (
          <Button onClick={onDropOff} className="w-full bg-blue-600 hover:bg-blue-700">
            <Package className="h-4 w-4 mr-2" />
            Mark as Delivered
          </Button>
        );
      default:
        return null;
    }
  };

  // Extract delivery instructions from notes
  const getDeliveryInstructions = () => {
    if (!order.notes) return null;
    
    const instructionMatch = order.notes.match(/Delivery Instructions: (.+?)(?:\s*\||$)/);
    return instructionMatch ? instructionMatch[1].trim() : null;
  };

  const deliveryInstructions = getDeliveryInstructions();

  // Minimized state - show small floating indicator
  if (isVisible && isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[99999]">
        <Button
          onClick={onMaximize}
          className="rounded-full h-14 w-14 bg-blue-600 hover:bg-blue-700 shadow-lg"
        >
          <Package className="h-6 w-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[99999]">
      <Card className="w-80 mx-4 animate-slide-up shadow-2xl">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-center flex-1">
              <h3 className="text-base font-semibold text-gray-900">{getStatusText()}</h3>
              <p className="text-xs text-gray-600">Order #{order.id.slice(0, 8)}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMinimize}
              className="h-8 w-8 p-0"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-3">
            {/* Customer Information */}
            <div className="bg-gray-50 p-2 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="h-4 w-4 text-gray-600" />
                <span className="font-medium">{order.customerName}</span>
              </div>
              <p className="text-sm text-gray-600 ml-6">{order.customerPhone}</p>
            </div>

            {/* Total Amount - Only show if available, no breakdown */}
            {order.totalAmount && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">Order Total</span>
                </div>
                <div className="ml-6">
                  <div className="flex justify-between">
                    <span className="font-semibold text-blue-900">Total:</span>
                    <span className="font-semibold text-blue-900">D{order.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Pickup Location */}
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-red-500 mt-1" />
              <div className="flex-1">
                <p className="font-medium text-sm">Pickup from:</p>
                <p className="text-sm text-gray-600">{order.pickupAddress}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate('pickup')}
                  className="mt-2 text-xs"
                >
                  <Navigation className="h-3 w-3 mr-1" />
                  Navigate
                </Button>
              </div>
            </div>

            {/* Delivery Location */}
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-green-500 mt-1" />
              <div className="flex-1">
                <p className="font-medium text-sm">Deliver to:</p>
                <p className="text-sm text-gray-600">{order.address}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate('dropoff')}
                  className="mt-2 text-xs"
                >
                  <Navigation className="h-3 w-3 mr-1" />
                  Navigate
                </Button>
              </div>
            </div>

            {/* Delivery Instructions */}
            {deliveryInstructions && (
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900 text-sm">Delivery Instructions:</p>
                    <p className="text-sm text-yellow-800 mt-1">{deliveryInstructions}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Time and Distance */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{order.estimatedTime}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{order.distance}</span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-6">
            {getActionButton()}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderPopup;
